
import { PlatformDetector, type PlatformInfo } from './platform-detector';
import { PlatformStrategies, type PlatformStrategy } from './platform-strategies';
import { ContextAnalyzer, type MessageContext } from './context-analyzer';
import { ConversationAnalyzer, type ConversationContext } from './conversation-analyzer';

export { PlatformDetector, type PlatformInfo } from './platform-detector';
export { PlatformStrategies, type PlatformStrategy } from './platform-strategies';
export { ContextAnalyzer, type MessageContext } from './context-analyzer';
export { ConversationAnalyzer, type ConversationContext } from './conversation-analyzer';

export class PlatformIntelligence {
  static analyze(merchantName: string, orderNumber?: string, issue?: string, amount?: number, region?: string) {
    const platformInfo = PlatformDetector.detectPlatform(merchantName, orderNumber, issue);
    const strategy = PlatformStrategies.getStrategy(platformInfo, amount || 0, region);
    const messageContext = ContextAnalyzer.analyzeMessageContext(merchantName, issue || '', platformInfo, orderNumber);
    
    return {
      platformInfo,
      strategy,
      messageContext,
      recommendations: this.generateRecommendations(platformInfo, strategy, messageContext, amount)
    };
  }

  static analyzeConversation(
    messages: any[],
    merchantName: string,
    userIssue: string,
    timeElapsedDays: number = 0
  ): ConversationContext {
    const platformInfo = PlatformDetector.detectPlatform(merchantName);
    const platformCategory = this.mapTransactionTypeToCategory(platformInfo.transactionType);
    
    return ConversationAnalyzer.analyzeConversation(
      messages,
      platformCategory,
      platformInfo.platform,
      userIssue,
      timeElapsedDays
    );
  }

  private static mapTransactionTypeToCategory(transactionType: string): string {
    const categoryMap: Record<string, string> = {
      'B2C': 'ecommerce',
      'C2C': 'ecommerce', 
      'subscription': 'subscription',
      'gig_platform': 'gig_platforms',
      'event_ticketing': 'event_ticketing',
      'accommodation': 'accommodation'
    };
    
    return categoryMap[transactionType] || 'ecommerce';
  }

  private static generateRecommendations(
    platformInfo: PlatformInfo, 
    strategy: PlatformStrategy, 
    messageContext: MessageContext,
    amount?: number
  ) {
    const recommendations = [];

    // Context-specific recommendations
    if (messageContext.targetRecipient === 'seller' && messageContext.relationship === 'peer_to_peer') {
      recommendations.push('Use friendly, understanding tone - you\'re communicating with an individual seller');
      recommendations.push('Be patient and reasonable - private sellers may have limited resources');
    }

    if (messageContext.targetRecipient === 'platform') {
      recommendations.push('Use formal tone and cite platform policies');
      recommendations.push('Reference buyer protection programs');
    }

    // Platform-specific recommendations
    if (platformInfo.platformSpecificData.hasMarketplaceProtection) {
      recommendations.push('Leverage platform buyer protection programs for stronger negotiating position');
    }

    if (platformInfo.isPrivateSeller && platformInfo.transactionType === 'C2C') {
      recommendations.push('Balance assertiveness with understanding of individual seller limitations');
    }

    if (platformInfo.platform === 'paypal' || platformInfo.platformSpecificData.disputeProcessAvailable) {
      recommendations.push('Consider platform dispute resolution before chargebacks');
    }

    // Amount-based recommendations
    if (amount && amount > 100) {
      recommendations.push('Document all communications for potential dispute escalation');
    }

    return recommendations;
  }
}
